import { inject, Injectable, signal } from '@angular/core';
import { WebSocketService } from '../../services/web-socket.service';
import { BehaviorSubject, from, merge, Observable, of } from 'rxjs';
import { filter, first, map, mergeMap, tap } from 'rxjs/operators';
import { TranslationService } from '../../shared/translation/translation.service';
import { SsWebSocketsPrivateChanel } from '../../vendor/ss-web-sockets/ss-web-sockets.types';
import { GamesService } from '../../services/games/games.service';
import {
  IGameLimitWS,
  IPaymentsWS,
  ISockets,
  ITournamentsStartWS,
  ITournamentsStatusItemWS,
  IUserGroupItemWS,
} from './notification-center.interface';
import { TournamentsService } from '../../../page/tournaments/tournaments.service';
import { GroupsService } from '../../services/groups.service';
import { LanguageService } from '../../services/language/language.service';
import additionalSocketsFields, { additionalLootboxFields, CustomNotificationChanel } from './additional-socket-fields';
import { LootboxService } from '../../services/lootbox/lootbox.service';
import { isArray, mapBonusTitle } from '../../helpers/utils';
import { InstallAppService } from '../../services/install-app.service';
import { CookieService, WindowService } from 'ngx-unificator/services';
import { UserService } from '../../services/user/user.service';
import { CustomNotificationCenterService } from './custom-notification-center.service';
import { CurrencyService } from '../../services/currency.service';

export enum GameLimitsReason {
  FORBIDDEN_WITH_BONUS = 'game_forbidden_with_bonus',
  BONUS_BET_LIMIT_EXCEEDED = 'bonus_bet_limit_exceeded',
  BET_LIMIT_EXCEEDED = 'bet_limit_exceeded',
  SESSION_LIMIT_EXCEEDED = 'session_limit_exceeded',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  private _ws = inject(WebSocketService);
  private _translate = inject(TranslationService);
  private _currency = inject(CurrencyService);
  private _games = inject(GamesService);
  private _tournaments = inject(TournamentsService);
  private _groups = inject(GroupsService);
  private _lang = inject(LanguageService);
  private _lootbox = inject(LootboxService);
  private _user = inject(UserService);
  private _window = inject(WindowService);
  private _installApp = inject(InstallAppService);
  private _cookie = inject(CookieService);
  private _customNotify = inject(CustomNotificationCenterService);

  public isShowMore = signal(false);

  /**
   * User for hide messages with same description
   * @private
   */
  private _previousDescriptionMessage = new Set<string>();

  /**
   * If true animate bell
   * @private
   */
  private _animateBell: boolean;

  /**
   * Is dropdown menu opened
   */
  public dropdownOpen: boolean;

  /**
   * Dropdown html element
   */
  public dropdownElement;

  /**
   * Tournaments statuses from WS
   * @private
   */
  private _tournaments$: Observable<ITournamentsStatusItemWS> = this._ws.tournaments$;

  private _tournamentsStarted$: Observable<ITournamentsStartWS> = this._ws.tournamentsStarted$;

  /**
   * Groups statuses from WS
   * @private
   */

  private _groups$: Observable<IUserGroupItemWS> = this._ws.groups$.pipe(
    filter(group => group && group.status && !this._groups.isExistGroup(group.id)),
  );

  /**
   * Game limits info from WS
   * @private
   */
  private _gameLimits$: Observable<IGameLimitWS> = this._ws.limits$;

  /**
   * Payments info from Ws
   * @private
   */
  private _payments$: Observable<IPaymentsWS> = this._ws.payments$;

  /**
   * All bonueses abservable
   * @private
   */
  private _allBonuses$: Observable<ISockets> = merge(this._ws.bonuses$, this._ws.lootboxes$, this._ws.freespins$).pipe(
    filter(this._filterBonusesNotification),
  );

  /**
   * Uses for display notification messages
   */
  public notifications$: BehaviorSubject<ISockets[]> = new BehaviorSubject<any>([]);

  /**
   * True if notification sidebar opened
   * @private
   */
  private _isOpened: boolean = false;

  private _isNotUnique: boolean = false;

  constructor() {}

  get animateBell() {
    return this._animateBell;
  }
  get isOpened() {
    return this._isOpened;
  }
  get isNotUnique() {
    return this._isNotUnique;
  }

  /**
   * Init notify
   */
  initNotify() {
    merge(
      this._allBonuses$,
      this._tournaments$,
      this._tournamentsStarted$,
      this._groups$,
      this._gameLimits$,
      this._payments$,
      ...this._customNotify.notificationSubjectList$
    ).pipe(
        filter(notification => !!notification),
        map((notification: ISockets | any) => isArray(notification) ? (notification?.filter(n => !!n)?.map(n => this._mapWSNotify(n))) : this._mapWSNotify(notification)),
        mergeMap(notification => notification instanceof Observable ? notification : Array.isArray(notification) ? from(notification) : of(notification)),
        filter(notification => !!notification),
        tap(notification => {
          this._animateBell = true;
          this._isNotUnique = false;
          this._previousDescriptionMessage.add(notification.description);
          this.notifications$.next([
            ...this._filterNotificationByUid([notification, ...this.notifications$.getValue()])?.sort((a, b) => (b.IsPinned ? 1 : 0) - (a.IsPinned ? 1 : 0)),
          ]);
        }),
        tap(() => (this._animateBell = false)),
      )
      .subscribe();

    this._customNotify.initCustomNotifications();
  }

  /**
   * Resolve notify by channel
   * @param notification
   * @private
   */
  private _mapWSNotify(notification: ISockets | any) {
    switch (notification.notify_channel) {
      case SsWebSocketsPrivateChanel.FREESPINS_CHANGED:
        return this._resolveNotifyFreeSpins(notification);
      case SsWebSocketsPrivateChanel.GAME_LIMITS:
        return this._resolveNotifyGameLimits(notification);
      case SsWebSocketsPrivateChanel.BONUSES_CHANGED:
        return this._resolveNotifyBonuses(notification);
      case SsWebSocketsPrivateChanel.PAYMENTS_CHANGED:
        return this._resolveNotifyPayments(notification);
      case SsWebSocketsPrivateChanel.LOOTBOXES_CHANGED:
        return this._resolveNotifyLootboxes(notification);
      case SsWebSocketsPrivateChanel.TOURNAMENTS_STATUSES:
        return this._resolveNotifyTournaments(notification);
      case SsWebSocketsPrivateChanel.TOURNAMENTS_STARTED:
        return this._resolveNotifyTournamentsStarted(notification);
      case SsWebSocketsPrivateChanel.GROUPS_UPDATES:
        return this._resolveNotifyGroups(notification);
      default:
        return notification;
    }
  }

  /**
   * Open dropdown menu
   */
  open() {
    if (!this.dropdownOpen) {
      this.dropdownOpen = true;
      this._isOpened = true;
      this._isNotUnique = true;
      this.dropdownElement.classList.add('open');
    }
  }

  /**
   * Close dropdown menu
   */
  close() {
    this.isShowMore.set(false);
    if (this.dropdownOpen) {
      this.dropdownOpen = false;
      this.dropdownElement.classList.remove('open');
    }
  }

  /**
   * Toggle dropdown menu
   */
  toggle() {
    this.isShowMore.set(false);
    this.dropdownOpen ? this.close() : this.open();
  }

  /**
   * Remove notify
   * @param notify
   */
  public removeNotify(notify) {
    if (notify.notify_channel === CustomNotificationChanel.INSTALL_APP) {
      this._installApp.closeNotificationBlock();
    }
    if (notify.notify_channel === CustomNotificationChanel.PROMO_CHANNEL) {
      this._cookie.set('promo-dynamic', 'true', 1, '/');
    }
    if (notify?.notify_channel === CustomNotificationChanel.RELOAD_BONUSES) {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      this._cookie.set('reload-bonus', '1', endOfDay, '/', (this._window.nativeWindow.location.hostname as any));
    }

    if (notify?.notify_channel === CustomNotificationChanel.WELCOME_BONUSES) {
      this._cookie.set(`welcome-bonus-${this._user.stats?.deposits_count}`, '1', 999, '/', (this._window.nativeWindow.location.hostname as any));
    }

    if (notify.notify_channel === CustomNotificationChanel.BLOG) {
      this._cookie.set(notify?.slug, '1');
    }

    this._previousDescriptionMessage.delete(notify.description);
    this.notifications$.next(this.notifications$.getValue().filter(oldNotify => notify.uid !== oldNotify.uid));
  }

  /**
   * Resolve notify FreeSpins
   * @param notify
   * @private
   */
  private _resolveNotifyFreeSpins(notify: any) {
    return {
      ...notify,
      description: this._translate.translate('t.notify-freespins-desc', {
        count: notify.freespins_total,
      }),
      title: mapBonusTitle(notify?.title),
      ...additionalSocketsFields[notify.notify_channel],
    };
  }

  /**
   * Resolve notify Bonuses
   * @param notify
   * @private
   */
  private _resolveNotifyBonuses(notify: any) {
    return {
      ...notify,
      description: this._translate.translate('t.notify-bonuses-desc', {
        bonus: mapBonusTitle(notify?.title),
      }),
      ...additionalSocketsFields[notify.notify_channel],
    };
  }

  /**
   * Resolve notify Lootboxes
   * @param notify
   * @private
   */
  private _resolveNotifyLootboxes(notify: any) {
    this._lootbox.lootboxesTranslates$
      .pipe(
        first(),
        map(() => {
          const translate = this._lootbox.resolveLootboxTranslates(notify);
          this.notifications$.next([
            ...this.notifications$.getValue(),
            {
              ...notify,
              description: this._translate.translate(
                (additionalLootboxFields[notify.title] && additionalLootboxFields[notify.title].description) || 't.notify-lootbox-desc',
                additionalLootboxFields[notify.title] && additionalLootboxFields[notify.title].description
                  ? null
                  : { bonus: this._translate.translate(translate.title) },
              ),
              title: translate?.title || notify?.title,
              link:
                (additionalLootboxFields[notify.title] && additionalLootboxFields[notify.title].link) ||
                additionalSocketsFields[notify.notify_channel].link,
              ...additionalSocketsFields[notify.notify_channel],
            },
          ]);
        }),
      )
      .subscribe();
  }

  /**
   * Resolve notify Payments
   * @param notify
   * @private
   */
  private _resolveNotifyPayments(notify: any) {
    return {
      ...notify,
      description: this._translate.translate(notify.success ? 't.notify-payments-desc-accepted' : 't.notify-payments-desc-discarded', {
        currency: notify.currency,
        payment_system: notify.payment_system,
        action: notify.action,
        amount_cents: this._currency.subunitsToUnits(notify.amount_cents, notify.currency),
      }),
      ...additionalSocketsFields[notify.notify_channel],
    };
  }

  /**
   * Resolve notify Groups
   * @param notify
   * @private
   */
  private _resolveNotifyGroups(notify: any) {
    const description = this._translate.translate('t.notify-groups-desc', {
      name: this._translate.translate(notify.name),
    });
    if (!this._previousDescriptionMessage.has(description)) {
      this._previousDescriptionMessage.add(description);
      return { ...notify, description, ...additionalSocketsFields[notify.notify_channel] };
    }

    return null;
  }

  private _resolveNotifyTournamentsStarted(notify: any) {
    return this._tournaments.list().pipe(
      filter(list => !!list),
      map(list => {
        const currentTournament = list.find(item => Number(item.identifier) === Number(notify.id));
        return currentTournament
          ? {
              ...notify,
              description: this._translate.translate('t.tournament-started', {
                title: currentTournament.Title,
              }),
              link: `/${this._lang.currentRouteLanguage}/tournaments/${currentTournament.slug}`,
              ...additionalSocketsFields[notify.notify_channel],
            }
          : null;
      }),
    );
  }

  /**
   * Resolve notify Tournaments
   * @param notify
   * @private
   */
  private _resolveNotifyTournaments(notify: any) {
    return this._tournaments.list().pipe(
      filter(list => !!list && list?.length),
      map(list => {
        const currentTournament = list.find(item => Number(item.identifier) === Number(notify.tournament_id));
        return currentTournament && Boolean(notify.award_place)
          ? {
              ...notify,
              description: this._translate.translate('t.notify-tournaments-desc', {
                place: notify.award_place,
                points: notify.points,
                title: currentTournament.Title,
              }),
              link: `/${this._lang.currentRouteLanguage}/tournaments/${currentTournament.slug}`,
              ...additionalSocketsFields[notify.notify_channel],
            }
          : null;
      }),
    );
  }

  /**
   * Resolve notify GameLimits
   * @param notify
   * @private
   */
  private _resolveNotifyGameLimits(notify: any) {
    return this._games.getGameByExternalId(notify.game).pipe(
      first(),
      map(game => {
        return game
          ? {
              ...notify,
              description: this._translate.translate(this._getTranslateForGameReason(notify.reason), {
                game: game.name,
                limit: notify.limit_cents ? this._currency.subunitsToUnits(notify.limit_cents, notify.currency) : '',
              }),
              ...additionalSocketsFields[notify.notify_channel],
            }
          : null;
      }),
    );
  }

  /**
   * Get translate key for game reason
   * @param reason
   * @private
   */
  private _getTranslateForGameReason(reason: GameLimitsReason) {
    return reason === GameLimitsReason.FORBIDDEN_WITH_BONUS
      ? 't.game-forbidden-with-bonus'
      : reason === GameLimitsReason.BONUS_BET_LIMIT_EXCEEDED
        ? 't.bonus-bet-limit'
        : reason === GameLimitsReason.BET_LIMIT_EXCEEDED
          ? 't.blocked-by-bonus'
          : reason === GameLimitsReason.SESSION_LIMIT_EXCEEDED
            ? 't.session-limit'
            : 't.game-reason-limit';
  }

  /**
   * Filter bonuses notifications
   * @param notify
   * @private
   */
  private _filterBonusesNotification(notify) {
    return notify.stage && notify.stage === 'issued' && (!notify.strategy || notify.strategy !== 'freespins_result');
  }

  private _filterNotificationByUid(array) {
    const seen = new Set<string | number>();
    return array.filter(item => {
      if (seen.has(item.uid)) return false;
      seen.add(item.uid);
      return true;
    });
  }
}
